/**
 * dateUtils.ts
 *
 * Timezone-safe date helpers for accounting date strings.
 *
 * ─── The Problem ──────────────────────────────────────────────────────────────
 * All dates stored in the database are plain calendar dates ("YYYY-MM-DD").
 * When JavaScript parses such a string with `new Date("2026-01-01")` it treats
 * it as **UTC midnight**.  Any browser running in a negative UTC offset
 * (e.g. EST = UTC-5, EDT = UTC-4) will then render that instant as the
 * *previous calendar day* via `toLocaleDateString`, producing "31/12/2025"
 * instead of "01/01/2026".
 *
 * ─── The Fix ──────────────────────────────────────────────────────────────────
 * We never construct a `Date` object from a plain date string.  Instead we
 * split the ISO string directly and reassemble it into the desired display
 * format.  No timezone conversion ever takes place, so the output is always
 * identical to the stored value regardless of the browser's locale or offset.
 *
 * All functions handle the following common input shapes:
 *   "YYYY-MM-DD"            – plain DB date (primary format)
 *   "YYYY-MM-DDTHH:mm:ssZ"  – ISO-8601 with time component (we strip it)
 *   ""  / null / undefined  – graceful fallback
 */

// ─── Display formatter ────────────────────────────────────────────────────────

/**
 * formatDateSafe
 *
 * Converts any "YYYY-MM-DD" (or ISO-8601) string to the Israeli display
 * format "DD/MM/YYYY" without ever constructing a `Date` object, so the
 * result is completely immune to browser timezone offsets.
 *
 * @param dateString  A date string in "YYYY-MM-DD" or ISO-8601 form.
 * @returns           "DD/MM/YYYY", or the original string if it cannot be parsed.
 *
 * @example
 *   formatDateSafe("2026-01-01")               // "01/01/2026"
 *   formatDateSafe("2025-12-31T00:00:00.000Z") // "31/12/2025"
 *   formatDateSafe("")                         // ""
 */
export function formatDateSafe(dateString: string | null | undefined): string {
  if (!dateString) return '';

  // Strip any time component ("T…") and trim whitespace
  const datePart = dateString.split('T')[0]?.trim() ?? '';

  // Expect exactly "YYYY-MM-DD"
  const parts = datePart.split('-');
  if (parts.length !== 3) return dateString; // unrecognised format – return as-is

  const [yyyy, mm, dd] = parts;

  // Basic sanity: all three segments must be numeric and non-empty
  if (!yyyy || !mm || !dd) return dateString;
  if (!/^\d{4}$/.test(yyyy) || !/^\d{1,2}$/.test(mm) || !/^\d{1,2}$/.test(dd)) {
    return dateString;
  }

  // Zero-pad day and month (handles both "YYYY-M-D" and "YYYY-MM-DD")
  const paddedMm = mm.padStart(2, '0');
  const paddedDd = dd.padStart(2, '0');

  return `${paddedDd}/${paddedMm}/${yyyy}`;
}

// ─── Sort comparator ──────────────────────────────────────────────────────────

/**
 * compareDateStrings
 *
 * Compares two "YYYY-MM-DD" strings lexicographically.  Because the ISO
 * format is naturally sortable as a plain string, this avoids constructing
 * `Date` objects (which would re-introduce the UTC-midnight shift) while
 * producing identical ordering to a numeric timestamp comparison.
 *
 * Returns a negative number, zero, or a positive number — identical
 * contract to `Array.prototype.sort`'s comparator.
 *
 * @example
 *   ["2026-03-01","2025-12-31"].sort(compareDateStrings) // ["2025-12-31","2026-03-01"]
 */
export function compareDateStrings(a: string | null | undefined, b: string | null | undefined): number {
  // Normalise: strip time component and treat null/undefined as empty string
  const da = (a ?? '').split('T')[0] ?? '';
  const db = (b ?? '').split('T')[0] ?? '';
  return da.localeCompare(db);
}
